import { Box, Heading, HStack, Link, Tag, Text, VStack } from '@chakra-ui/react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import BlogMarkdown from '../../components/BlogMarkdown';
import MarketingPageShell, {
  breadcrumbSchema,
} from '../../components/MarketingPageShell';
import {
  blogPostingSchema,
  getAllPosts,
  getPostBySlug,
  postCanonicalUrl,
  postDateModified,
  postDescription,
  postOgImage,
  postPageTitle,
} from '../../lib/blog';
import { buildSignupUrl, trackSignupClick } from '../../utils/analytics';

const APP_URL = 'https://app.wagerwatchdog.com';
const SIGNUP_URL = `${APP_URL}/signup`;

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Blog', path: '/blog' },
    { name: post.title, path: `/blog/${post.slug}` },
  ];

  const related = getAllPosts()
    .filter(
      (p) =>
        p.slug !== post.slug &&
        (p.category === post.category ||
          p.tags?.some((t) => post.tags?.includes(t))),
    )
    .slice(0, 3);

  return (
    <MarketingPageShell
      fullTitle={postPageTitle(post)}
      description={postDescription(post)}
      canonicalUrl={postCanonicalUrl(post)}
      article={{
        ogType: 'article',
        ogImage: postOgImage(post),
        ogImageAlt: post.imageAlt,
        publishedTime: post.date,
        modifiedTime: postDateModified(post),
        author: post.author,
        tags: post.tags,
        section: post.category,
        twitterCard: 'summary_large_image',
      }}
      schema={[
        blogPostingSchema(post),
        {
          '@context': 'https://schema.org',
          ...breadcrumbSchema(breadcrumbs),
        },
      ]}
      breadcrumbs={breadcrumbs}
      h1={post.title}
      lead={post.excerpt}
    >
      <VStack align="start" spacing={4} w="full">
        <HStack spacing={3} flexWrap="wrap" color="gray.500" fontSize="sm">
          <Text>
            Published {post.date}
            {post.updated && post.updated !== post.date
              ? ` · Updated ${post.updated}`
              : ''}
          </Text>
          {post.author ? <Text>· {post.author}</Text> : null}
        </HStack>
        {post.tags && post.tags.length > 0 ? (
          <HStack spacing={2} flexWrap="wrap">
            {post.tags.map((tag) => (
              <Tag key={tag} size="sm" colorScheme="blue" variant="subtle">
                {tag}
              </Tag>
            ))}
          </HStack>
        ) : null}
      </VStack>

      <BlogMarkdown content={post.body} />

      <Box
        p={{ base: 6, md: 8 }}
        borderRadius="16px"
        border="1px solid"
        borderColor="whiteAlpha.100"
        bg="linear-gradient(135deg, rgba(10,112,185,0.12) 0%, rgba(31,41,55,0.9) 55%)"
        w="full"
      >
        <Heading as="h2" fontSize="lg" color="white" mb={3}>
          Explore WagerWatchdog
        </Heading>
        <Text color="gray.300" lineHeight="1.8" mb={2}>
          See full expert ratings and sport leaderboards in the app:{' '}
          <Link
            href={buildSignupUrl(SIGNUP_URL)}
            color="primary.400"
            isExternal
            onClick={() => trackSignupClick('blog_post_footer')}
          >
            Sign up free
          </Link>
          .
        </Text>
        <Text color="gray.400" fontSize="sm" lineHeight="1.8">
          <Link as={RouterLink} to="/about" color="primary.400">
            About us
          </Link>
          {' · '}
          <Link as={RouterLink} to="/contact" color="primary.400">
            Contact
          </Link>
          {' · '}
          <Link href={`${APP_URL}/nba`} color="primary.400" isExternal>
            NBA ratings
          </Link>
        </Text>
      </Box>

      {related.length > 0 ? (
        <Box w="full">
          <Heading as="h2" fontSize="lg" color="white" mb={4}>
            Related posts
          </Heading>
          <VStack align="stretch" spacing={3}>
            {related.map((r) => (
              <Link
                key={r.slug}
                as={RouterLink}
                to={`/blog/${r.slug}`}
                color="primary.400"
                fontWeight="medium"
              >
                {r.title}
              </Link>
            ))}
          </VStack>
        </Box>
      ) : null}
    </MarketingPageShell>
  );
}
